import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { filter, trim } from "lodash";
import Button from "@shared/v2/Button";
import FieldLabel from "@shared/v2/FieldLabel";
import Modal from "@shared/v2/Modal";
import TextInput from "@shared/v2/TextInput";
import Toggle from "@shared/v2/Toggle";
import Typography from "@shared/v2/Typography";
import { useDebounce, useToggleUsers, useCanSaveModel, useSetValue } from "../helpers";
import RemoveableUser from "./RemoveableUser";
import UserSelect from "./UserSelect";
import DeleteConfirm from "./DeleteConfirm";

import * as actions from "../actions/default";
import * as apiActions from "../actions/apiActions";

const PondModal = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector((state) => state.leadPonds.isModalOpen);
  const isSaving = useSelector((state) => state.leadPonds.isSaving);
  const hasSaveError = useSelector((state) => state.leadPonds.hasSaveError);
  const pond = useSelector((state) => state.leadPonds.pondData);
  const isLoadingUsers = useSelector((state) => state.leadPonds.isLoadingUsers);
  const usersCollection = useSelector((state) => state.leadPonds.usersCollection);
  const hasDuplicate = useSelector((state) => state.leadPonds.hasDuplicate);
  const [model, setModel] = useState({});
  const [showConfirm, setConfirm] = useState(0);
  const debouncedDuplicateTerm = useDebounce(model.name, 500);
  const [saveClicked, setSaveClicked] = useState(0);
  const userOptions = useMemo(
    () => usersCollection?.map((u) => ({ label: u.name, value: { ...u } })) || [],
    [usersCollection],
  );

  useEffect(() => {
    setModel(pond ? { ...pond } : { name: "", display_agent: null, accessible_by_users: [] });
  }, [pond]);

  useEffect(() => {
    dispatch(apiActions.getAssignableUsers());
  }, []);

  useEffect(() => {
    dispatch(apiActions.duplicateCheck(debouncedDuplicateTerm));
  }, [debouncedDuplicateTerm]);

  const closePondModal = () => dispatch(actions.closePondModal());

  useEffect(() => {
    if (saveClicked && !isSaving && !hasSaveError) {
      closePondModal();
    }
  }, [isSaving]);
  // Change the model based on key
  const setValue = useSetValue(setModel);

  const setAgent = (agent) => {
    setValue("display_agent", {
      ...model.display_agent,
      ...agent,
    });
  };

  const toggleUser = useToggleUsers(model.accessible_by_users, (col) => {
    setValue("accessible_by_users", [...col]);
  });

  // Close the confirmation modal
  const closeConfirm = () => {
    setConfirm(0);
  };

  const confirmDelete = () => {
    dispatch(apiActions.removePond(model.id));
    closeConfirm();
    closePondModal();
  };

  // Flag if this is a new pond
  const isNew = model.id == null;
  // Flag if the model is saveable
  const canSave = useCanSaveModel(pond, model, hasDuplicate);
  return (
    <Modal
      show={isOpen}
      onHide={closePondModal}
      dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
      contentClassName="tw-max-w-[640px] tw-max-h-[90vh] tw-w-full tw-flex tw-flex-col tw-gap-[32px]"
    >
      <Modal.Header title={`${isNew ? "New" : "Edit"} Lead Pond`} closeButton />
      <Modal.Body className="tw-flex-1 tw-overflow-auto tw-flex tw-flex-col tw-gap-[24px]">
        <TextInput
          label="Pond Name"
          data-cy="lp-name-label"
          id="lp-name"
          value={model.name}
          onChange={(e) => setValue("name", e.target.value)}
          placeholder="Enter the name for the lead pond"
          isRequired={!trim(model.name)}
          error={hasDuplicate ? "A pond with the same name already exists." : null}
        />

        <div className="tw-flex tw-flex-col tw-gap-[8px]">
          <FieldLabel label="Display Agent" htmlFor="lp-agent" isRequired={!model.display_agent} />
          <Typography>
            Choose a user to display their photo, name, email, and phone in any auto emails sent to the leads
            in the Lead Pond, including Listing Alerts.
          </Typography>
          {!model.display_agent ? (
            <UserSelect
              value={model.agent}
              isLoading={isLoadingUsers}
              options={userOptions}
              onSelect={setAgent}
              data-cy="lp-agent-select"
            />
          ) : (
            <RemoveableUser
              name={model.display_agent.name}
              imageUrl={model.display_agent.image_url}
              image={model.display_agent.image}
              onClick={() => {
                // delete it from the model
                setValue("display_agent", null, true);
              }}
            />
          )}
        </div>

        <div className="tw-flex tw-flex-col tw-gap-[8px]">
          <FieldLabel
            label="Pond Users"
            htmlFor="lp-users"
            isRequired={!model.accessible_by_users || model.accessible_by_users.length === 0}
          />
          <Typography>
            Choose which users will have access to this pond. Account admins will automatically have access to
            this Pond.
          </Typography>
          <Toggle
            id="notify_agents_of_new_leads"
            name="notify_agents_of_new_leads"
            value={model.notify_agents_of_new_leads}
            checked={model.notify_agents_of_new_leads === true}
            disabled={false}
            onChange={() => {
              setValue("notify_agents_of_new_leads", !model.notify_agents_of_new_leads);
            }}
            text="Notify agents of new leads"
          />
          <UserSelect
            value={model.accessible_by_users}
            isLoading={isLoadingUsers}
            isMultiSelect
            options={userOptions.filter((u) => u.value.user_type !== "Admin")}
            onSelect={toggleUser}
            data-cy="lp-users-select"
          />
          {model.accessible_by_users?.map((user) => (
            <RemoveableUser
              key={user.uuid}
              name={user.name}
              imageUrl={user.image_url || user.image}
              onClick={() => {
                // delete it from the model
                const cleared = filter(model.accessible_by_users, (u) => u.uuid !== user.uuid);
                setValue("accessible_by_users", cleared);
              }}
            />
          ))}
        </div>
      </Modal.Body>

      <Modal.Footer className="tw-flex tw-justify-between tw-gap-[16px]">
        <Button size="medium" schema="tertiary" onClick={closePondModal}>
          Cancel
        </Button>

        {isNew ? (
          <Button
            size="medium"
            data-cy="lp-submit"
            isLoading={isSaving}
            disabled={!canSave}
            onClick={() => {
              dispatch(apiActions.createPond(model));
              setSaveClicked(true);
            }}
          >
            Create Pond
          </Button>
        ) : (
          <div className="tw-flex tw-gap-[8px]">
            <Button data-cy="lp-remove" size="medium" schema="warning" onClick={() => setConfirm(1)}>
              Delete Pond
            </Button>
            <Button
              data-cy="lp-submit"
              size="medium"
              isLoading={isSaving}
              disabled={!canSave}
              onClick={() => {
                dispatch(apiActions.updatePond(model));
                setSaveClicked(true);
              }}
            >
              Save
            </Button>
          </div>
        )}
      </Modal.Footer>
      <DeleteConfirm show={showConfirm} onClose={closeConfirm} onConfirm={confirmDelete} />
    </Modal>
  );
};

export default PondModal;
